import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  Loader2,
  Trophy,
  ExternalLink,
  CheckCircle2,
  XCircle,
  AlertCircle,
  TrendingUp,
  TrendingDown,
  Star,
  BarChart3,
  FileText,
  RefreshCw,
  Info,
  Lightbulb,
  Quote,
  Sparkles,
  Layers,
  FlaskConical,
  Brain,
  Shield,
  Target,
  Zap,
  Download,
  AlertTriangle,
} from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { authFetch, handleResponse } from '@/utils/auth-fetch';

interface ComparisonResultsProps {
  comparisonId: string;
  onRerunWithManualContent?: (manualContent: Array<{ url: string; html: string }>) => void;
}

interface PageAnalysis {
  analysis_id: string;
  page_type: 'generated' | 'competitor' | 'serp_result' | 'serp_leader' | 'current_page';
  url: string;
  title: string;
  aeo_score: number;
  seo_score: number;
  ux_score: number;
  overall_score: number;
  aeo_scores: Record<string, number>;
  seo_scores: Record<string, number>;
  ux_scores: Record<string, number>;
  strengths: string[];
  weaknesses: string[];
  screenshot_url?: string;
  serp_position?: number | null;
}

interface ContextualExample {
  original: string;
  category: 'AEO' | 'SEO' | 'UX';
  example: string;
  explanation: string;
  suggestion?: string;
}

interface ContextualExamplesData {
  analysisId: string;
  pageTitle: string;
  pageUrl: string;
  scores: {
    aeo: number;
    seo: number;
    ux: number;
    overall: number;
  };
  examples: {
    strengths_examples: ContextualExample[];
    weaknesses_examples: ContextualExample[];
  };
}

interface AeoSectionConfig {
  mode: 'full' | 'partial';
  sections: string[];
}

// Scientific Metrics interfaces
interface ScientificPageResult {
  url: string;
  label: string;
  pageType: string;
  scores: {
    composite: number;
    semanticDensity: number;
    answerCoverage: number;
    citationReadiness: number;
    specCompleteness: number;
  };
  advancedMetrics?: {
    ragRetrieval?: {
      score: number;
      avgRelevance: number;
      topChunks: number;
    };
    hallucinationPrevention?: {
      score: number;
      factualGrounding: number;
      verifiableClaims: number;
    };
  };
}

interface ScientificMetricsData {
  comparison: {
    urls: string[];
    results: ScientificPageResult[];
    winners: {
      overall: string;
      semanticDensity: string;
      answerCoverage: string;
      ragRetrieval?: string;
      hallucinationPrevention?: string;
    };
  };
  insights: Array<{
    type: 'strength' | 'weakness' | 'opportunity';
    metric: string;
    message: string;
  }>;
  metadata: {
    duration_ms: number;
    timestamp: string;
  };
}

interface ComparisonData {
  id: string;
  site_id: string;
  serp_query: string;
  serp_country: string;
  created_at: string;
  final_verdict: string;
  rankings: {
    aeo_winner: string;
    seo_winner: string;
    ux_winner: string;
    overall_winner: string;
    aeo_section_config?: AeoSectionConfig | null;
  };
  page_analyses: any[];
  analyses_detail: PageAnalysis[];
  // #3825 CR-1 BLOCK: competitors dropped or SSRF-blocked during the run,
  // surfaced top-level by getComparison so they're visible to the user.
  skipped_competitors?: Array<{ url: string; type?: string; reason?: string; ssrf_blocked?: boolean }>;
  aeo_winner?: string;
  seo_winner?: string;
  ux_winner?: string;
  overall_winner?: string;
}

export function ComparisonResults({ comparisonId, onRerunWithManualContent }: ComparisonResultsProps) {
  const [comparison, setComparison] = useState<ComparisonData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedAnalysis, setSelectedAnalysis] = useState<string | null>(null);
  const [manualContent, setManualContent] = useState<Array<{ url: string; html: string; type?: string; label?: string }>>([]);
  const [showManualForm, setShowManualForm] = useState<string | null>(null); // analysis_id being edited
  const [currentManualUrl, setCurrentManualUrl] = useState('');
  const [currentManualHtml, setCurrentManualHtml] = useState('');
  const [currentManualType, setCurrentManualType] = useState<string>('');
  const [currentManualLabel, setCurrentManualLabel] = useState<string>('');

  // Contextual examples state
  const [showExamplesDialog, setShowExamplesDialog] = useState(false);
  const [loadingExamples, setLoadingExamples] = useState(false);
  const [contextualExamples, setContextualExamples] = useState<ContextualExamplesData | null>(null);
  const [examplesError, setExamplesError] = useState<string | null>(null);

  // Scientific metrics state
  const [scientificMetrics, setScientificMetrics] = useState<ScientificMetricsData | null>(null);
  const [loadingScientificMetrics, setLoadingScientificMetrics] = useState(false);
  const [scientificMetricsError, setScientificMetricsError] = useState<string | null>(null);

  useEffect(() => {
    loadComparison();
  }, [comparisonId]);

  const loadComparison = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await authFetch(`/api/aeo-benchmark/results/${comparisonId}`);
      const data = await handleResponse<{ comparison: ComparisonData }>(response);

      console.log('Loaded comparison:', data.comparison);
      setComparison(data.comparison);

      // Auto-select first analysis
      if (data.comparison.analyses_detail.length > 0) {
        setSelectedAnalysis(data.comparison.analyses_detail[0].analysis_id);
      }
    } catch (err: any) {
      console.error('Failed to load comparison:', err);
      setError(err.message || 'Failed to load comparison results');
    } finally {
      setLoading(false);
    }
  };

  const getWinnerAnalysis = (winnerId?: string): PageAnalysis | undefined => {
    if (!winnerId || !comparison) return undefined;
    return comparison.analyses_detail.find(a => a.analysis_id === winnerId);
  };

  // Fetch contextual examples for a page analysis
  const fetchContextualExamples = async (analysisId: string) => {
    setLoadingExamples(true);
    setExamplesError(null);
    setShowExamplesDialog(true);

    try {
      const response = await authFetch(`/api/aeo-benchmark/contextual-examples/${analysisId}`, {
        method: 'POST',
      });
      const data = await handleResponse<ContextualExamplesData>(response);
      setContextualExamples(data);
    } catch (err: any) {
      console.error('Failed to fetch contextual examples:', err);
      setExamplesError(err.message || 'Failed to fetch contextual examples');
    } finally {
      setLoadingExamples(false);
    }
  };

  // Run scientific metrics analysis
  const runScientificMetrics = async () => {
    if (!comparison) return;

    setLoadingScientificMetrics(true);
    setScientificMetricsError(null);

    try {
      // Prepare URLs for comparison - get all pages from the analysis
      const urls = comparison.analyses_detail.map(analysis => ({
        url: analysis.url,
        label: getPageTypeLabel(analysis.page_type),
        pageType: analysis.page_type,
      }));

      const response = await authFetch('/api/aeo-benchmark/scientific-metrics', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          urls,
          categoryPath: comparison.serp_query, // Use query as category context
          options: {
            runAdvancedMetrics: true,
            runMultiModel: false,
          },
        }),
      });

      const data = await handleResponse<ScientificMetricsData>(response);
      setScientificMetrics(data);
    } catch (err: any) {
      console.error('Failed to run scientific metrics:', err);
      setScientificMetricsError(err.message || 'Failed to run scientific metrics analysis');
    } finally {
      setLoadingScientificMetrics(false);
    }
  };

  // Helper to get score color for scientific metrics (0-1 scale)
  const getScientificScoreColor = (score: number): string => {
    if (score >= 0.8) return 'text-green-600';
    if (score >= 0.6) return 'text-yellow-600';
    return 'text-red-600';
  };

  // Helper to format scientific scores as percentage
  const formatScientificScore = (score: number): string => {
    return `${Math.round(score * 100)}%`;
  };

  // Download HTML content for a page
  const downloadPageHtml = async (url: string, label: string) => {
    try {
      // Fetch HTML through our proxy to avoid CORS issues
      const response = await authFetch('/api/aeo-benchmark/fetch-html', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url }),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch HTML');
      }

      const data = await response.json();
      const html = data.html || '';

      // Create blob and download
      const blob = new Blob([html], { type: 'text/html' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `${label.replace(/[^a-z0-9]/gi, '_')}_${new Date().toISOString().split('T')[0]}.html`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      console.error('Failed to download HTML:', err);
      // Fallback: open URL in new tab so user can save manually
      window.open(url, '_blank');
    }
  };

  const getPageTypeLabel = (type: string, showPartial = false): string => {
    switch (type) {
      case 'generated':
        return showPartial && isPartialAeo ? 'Partial AEO Page' : 'Your AEO Page';
      case 'competitor':
      case 'competitor_manual':
        return 'Competitor';
      case 'serp_result':
      case 'serp_leader':
        return 'SERP Leader';
      case 'current_page':
        return 'Current Page';
      default:
        return type;
    }
  };

  const getPageTypeBadge = (type: string) => {
    const variants: Record<string, string> = {
      generated: 'default',
      competitor: 'secondary',
      competitor_manual: 'secondary',
      serp_result: 'outline',
      serp_leader: 'outline',
      current_page: 'secondary',
    };
    return variants[type] || 'secondary';
  };

  const getScoreColor = (score: number): string => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBadgeVariant = (score: number): 'default' | 'secondary' | 'destructive' => {
    if (score >= 80) return 'default';
    if (score >= 60) return 'secondary';
    return 'destructive';
  };

  const getDomainFromUrl = (url: string): string => {
    try {
      const urlObj = new URL(url);
      return urlObj.hostname.replace('www.', '');
    } catch {
      return url;
    }
  };

  const isFailedPage = (analysis: PageAnalysis): boolean => {
    return analysis.overall_score < 10;
  };

  const handleAddManualContent = (url: string) => {
    if (!currentManualHtml.trim()) {
      return;
    }
    setManualContent([...manualContent, {
      url,
      html: currentManualHtml,
      type: currentManualType || 'competitor_manual',
      label: currentManualLabel || 'Manual Content',
    }]);
    setShowManualForm(null);
    setCurrentManualUrl('');
    setCurrentManualHtml('');
    setCurrentManualType('');
    setCurrentManualLabel('');
  };

  const handleRerunAnalysis = () => {
    if (onRerunWithManualContent && manualContent.length > 0) {
      onRerunWithManualContent(manualContent);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        <span className="ml-2 text-muted-foreground">Loading results...</span>
      </div>
    );
  }

  if (error || !comparison) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>{error || 'No comparison data found'}</AlertDescription>
      </Alert>
    );
  }

  const aeoWinner = getWinnerAnalysis(comparison.aeo_winner || comparison.rankings?.aeo_winner);
  const seoWinner = getWinnerAnalysis(comparison.seo_winner || comparison.rankings?.seo_winner);
  const overallWinner = getWinnerAnalysis(comparison.overall_winner || comparison.rankings?.overall_winner);

  // Check if this is a partial AEO comparison
  const aeoSectionConfig = comparison.rankings?.aeo_section_config;
  const isPartialAeo = aeoSectionConfig?.mode === 'partial';
  const partialSectionCount = aeoSectionConfig?.sections?.length || 0;

  return (
    <div className="space-y-6">
      {/* Partial AEO Mode Banner */}
      {isPartialAeo && (
        <Alert className="border-blue-200 bg-blue-50 dark:bg-blue-950/20">
          <Layers className="h-4 w-4 text-blue-600" />
          <AlertDescription className="flex items-center justify-between">
            <div>
              <span className="font-medium text-blue-800 dark:text-blue-200">
                Partial AEO Benchmark
              </span>
              <span className="text-blue-700 dark:text-blue-300 ml-2">
                Your page was enhanced with {partialSectionCount} AEO section{partialSectionCount !== 1 ? 's' : ''}
              </span>
            </div>
            <Badge variant="outline" className="bg-blue-100 text-blue-700 border-blue-300">
              <Layers className="h-3 w-3 mr-1" />
              {partialSectionCount} sections
            </Badge>
          </AlertDescription>
        </Alert>
      )}

      {/* Winners Overview */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5 text-yellow-500" />
            Competition Winners
          </CardTitle>
          <CardDescription>
            Query: {comparison.serp_query} • Market: {comparison.serp_country}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium">AEO Winner</CardTitle>
              </CardHeader>
              <CardContent>
                {aeoWinner ? (
                  <>
                    <div className="text-2xl font-bold flex items-center gap-2">
                      {aeoWinner.aeo_score}
                      <Badge variant={getScoreBadgeVariant(aeoWinner.aeo_score)}>
                        /100
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1 truncate">
                      {aeoWinner.title}
                    </p>
                    <Badge variant={getPageTypeBadge(aeoWinner.page_type)} className="mt-2">
                      {getPageTypeLabel(aeoWinner.page_type)}
                    </Badge>
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground">N/A</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium">SEO Winner</CardTitle>
              </CardHeader>
              <CardContent>
                {seoWinner ? (
                  <>
                    <div className="text-2xl font-bold flex items-center gap-2">
                      {seoWinner.seo_score}
                      <Badge variant={getScoreBadgeVariant(seoWinner.seo_score)}>
                        /100
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1 truncate">
                      {seoWinner.title}
                    </p>
                    <Badge variant={getPageTypeBadge(seoWinner.page_type)} className="mt-2">
                      {getPageTypeLabel(seoWinner.page_type)}
                    </Badge>
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground">N/A</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium">Overall Winner</CardTitle>
              </CardHeader>
              <CardContent>
                {overallWinner ? (
                  <>
                    <div className="text-2xl font-bold flex items-center gap-2">
                      {overallWinner.overall_score}
                      <Badge variant={getScoreBadgeVariant(overallWinner.overall_score)}>
                        /100
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1 truncate">
                      {overallWinner.title}
                    </p>
                    <Badge variant={getPageTypeBadge(overallWinner.page_type)} className="mt-2">
                      {getPageTypeLabel(overallWinner.page_type)}
                    </Badge>
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground">N/A</p>
                )}
              </CardContent>
            </Card>
          </div>
        </CardContent>
      </Card>

      {/* AI Verdict */}
      {comparison.final_verdict && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Star className="h-5 w-5 text-yellow-500" />
              AI Expert Verdict
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm leading-relaxed whitespace-pre-line">
              {comparison.final_verdict}
            </p>
          </CardContent>
        </Card>
      )}

      {/* #3825 CR-1 BLOCK: competitors excluded from the comparison (dropped
          or SSRF-blocked) — surfaced so they never silently disappear. */}
      {comparison.skipped_competitors && comparison.skipped_competitors.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-amber-600 dark:text-amber-500">
              <AlertTriangle className="h-5 w-5" />
              Excluded from comparison ({comparison.skipped_competitors.length})
            </CardTitle>
            <CardDescription>
              These pages were requested but could not be safely or successfully fetched.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {comparison.skipped_competitors.map((sc, i) => (
                <li key={i} className="text-sm flex flex-col gap-0.5 border-l-2 border-amber-400 pl-3">
                  <span className="font-mono break-all">{sc.url}</span>
                  <span className="text-muted-foreground">
                    {sc.ssrf_blocked ? 'Rejected as unsafe (private/internal address)' : (sc.reason || 'Could not be fetched')}
                    {sc.type ? ` • ${sc.type}` : ''}
                  </span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Scores Table */}
      <Card>
        <CardHeader>
          <CardTitle>Detailed Scores</CardTitle>
          <CardDescription>
            Comparison of {comparison.analyses_detail.length} pages
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Page</TableHead>
                <TableHead>Type</TableHead>
                <TableHead className="text-center">SERP Rank</TableHead>
                <TableHead className="text-center">AEO</TableHead>
                <TableHead className="text-center">SEO</TableHead>
                <TableHead className="text-center">UX</TableHead>
                <TableHead className="text-center">Overall</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {comparison.analyses_detail.map((analysis) => {
                const failed = isFailedPage(analysis);
                const domain = getDomainFromUrl(analysis.url);
                const hasManualContent = manualContent.some(mc => mc.url === analysis.url);

                return (
                <TableRow key={analysis.analysis_id} className={failed ? 'bg-red-50 dark:bg-red-950/20' : ''}>
                  <TableCell>
                    <div>
                      <p className="font-medium truncate max-w-xs">
                        {analysis.title}
                      </p>
                      <div className="flex items-center gap-2">
                        <a
                          href={analysis.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-muted-foreground hover:underline truncate max-w-xs"
                        >
                          {domain}
                        </a>
                        {failed && (
                          <Badge variant="destructive" className="text-xs">
                            Failed
                          </Badge>
                        )}
                        {hasManualContent && (
                          <Badge variant="default" className="text-xs">
                            Manual Added
                          </Badge>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={getPageTypeBadge(analysis.page_type)}>
                      {analysis.page_type === 'competitor' || analysis.page_type === 'competitor_manual'
                        ? domain
                        : getPageTypeLabel(analysis.page_type)}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-center">
                    {analysis.page_type === 'generated' ? (
                      <span className="text-muted-foreground text-sm">N/A</span>
                    ) : analysis.serp_position ? (
                      <Badge variant="outline" className="font-semibold">
                        #{analysis.serp_position}
                      </Badge>
                    ) : (
                      <span className="text-muted-foreground text-sm">-</span>
                    )}
                  </TableCell>
                  <TableCell className="text-center">
                    <span className={`font-semibold ${getScoreColor(analysis.aeo_score)}`}>
                      {analysis.aeo_score}
                    </span>
                  </TableCell>
                  <TableCell className="text-center">
                    <span className={`font-semibold ${getScoreColor(analysis.seo_score)}`}>
                      {analysis.seo_score}
                    </span>
                  </TableCell>
                  <TableCell className="text-center">
                    <span className={`font-semibold ${getScoreColor(analysis.ux_score)}`}>
                      {analysis.ux_score}
                    </span>
                  </TableCell>
                  <TableCell className="text-center">
                    <span className={`font-bold ${getScoreColor(analysis.overall_score)}`}>
                      {analysis.overall_score}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {failed && !hasManualContent && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setShowManualForm(analysis.analysis_id);
                            setCurrentManualUrl(analysis.url);
                            setCurrentManualType(analysis.page_type);
                            setCurrentManualLabel(getPageTypeLabel(analysis.page_type));
                          }}
                        >
                          <FileText className="h-4 w-4 mr-1" />
                          Add HTML
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => window.open(analysis.url, '_blank')}
                        title="Open page in new tab"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Re-run Analysis Alert - shown right after Detailed Scores */}
      {manualContent.length > 0 && onRerunWithManualContent && (
        <Alert className="border-2 border-green-500 bg-green-50 dark:bg-green-950/20">
          <RefreshCw className="h-4 w-4 text-green-600" />
          <AlertDescription className="flex items-center justify-between w-full">
            <span className="text-green-800 dark:text-green-200">
              <strong>{manualContent.length}</strong> page{manualContent.length > 1 ? 's' : ''} with manual content ready for re-analysis
            </span>
            <Button
              onClick={handleRerunAnalysis}
              size="sm"
              className="ml-4"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Re-run Analysis
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Comprehensive Metrics Comparison */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Comprehensive Metrics Comparison
          </CardTitle>
          <CardDescription>
            All metrics side-by-side to identify who's doing best in each area
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[200px] sticky left-0 bg-background">Metric</TableHead>
                  {comparison.analyses_detail.map((analysis) => (
                    <TableHead key={analysis.analysis_id} className="text-center min-w-[120px]">
                      <div className="space-y-1">
                        <Badge variant={getPageTypeBadge(analysis.page_type)} className="text-xs">
                          {getPageTypeLabel(analysis.page_type)}
                        </Badge>
                        <p className="text-xs font-normal truncate max-w-[120px]">
                          {getDomainFromUrl(analysis.url)}
                        </p>
                      </div>
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {/* AEO Score Category */}
                <TableRow className="bg-blue-50 dark:bg-blue-950/20">
                  <TableCell colSpan={comparison.analyses_detail.length + 1} className="font-bold sticky left-0 bg-blue-50 dark:bg-blue-950/20">
                    AEO Score
                  </TableCell>
                </TableRow>
                {Object.keys(comparison.analyses_detail[0]?.aeo_scores || {}).map((metric) => (
                  <TableRow key={`aeo-${metric}`}>
                    <TableCell className="sticky left-0 bg-background capitalize pl-6">
                      {metric.replace(/_/g, ' ')}
                    </TableCell>
                    {comparison.analyses_detail.map((analysis) => {
                      const score = analysis.aeo_scores?.[metric] || 0;
                      const isHighest = score === Math.max(...comparison.analyses_detail.map(a => a.aeo_scores?.[metric] || 0));
                      return (
                        <TableCell key={analysis.analysis_id} className="text-center">
                          <span className={`font-semibold ${isHighest ? 'text-green-600' : getScoreColor(score)}`}>
                            {score}
                            {isHighest && <Trophy className="inline h-3 w-3 ml-1" />}
                          </span>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}

                {/* SEO Score Category */}
                <TableRow className="bg-purple-50 dark:bg-purple-950/20">
                  <TableCell colSpan={comparison.analyses_detail.length + 1} className="font-bold sticky left-0 bg-purple-50 dark:bg-purple-950/20">
                    SEO Score
                  </TableCell>
                </TableRow>
                {Object.keys(comparison.analyses_detail[0]?.seo_scores || {}).map((metric) => (
                  <TableRow key={`seo-${metric}`}>
                    <TableCell className="sticky left-0 bg-background capitalize pl-6">
                      {metric.replace(/_/g, ' ')}
                    </TableCell>
                    {comparison.analyses_detail.map((analysis) => {
                      const score = analysis.seo_scores?.[metric] || 0;
                      const isHighest = score === Math.max(...comparison.analyses_detail.map(a => a.seo_scores?.[metric] || 0));
                      return (
                        <TableCell key={analysis.analysis_id} className="text-center">
                          <span className={`font-semibold ${isHighest ? 'text-green-600' : getScoreColor(score)}`}>
                            {score}
                            {isHighest && <Trophy className="inline h-3 w-3 ml-1" />}
                          </span>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}

                {/* UX Score Category */}
                <TableRow className="bg-orange-50 dark:bg-orange-950/20">
                  <TableCell colSpan={comparison.analyses_detail.length + 1} className="font-bold sticky left-0 bg-orange-50 dark:bg-orange-950/20">
                    UX Score
                  </TableCell>
                </TableRow>
                {Object.keys(comparison.analyses_detail[0]?.ux_scores || {}).map((metric) => (
                  <TableRow key={`ux-${metric}`}>
                    <TableCell className="sticky left-0 bg-background capitalize pl-6">
                      {metric.replace(/_/g, ' ')}
                    </TableCell>
                    {comparison.analyses_detail.map((analysis) => {
                      const score = analysis.ux_scores?.[metric] || 0;
                      const isHighest = score === Math.max(...comparison.analyses_detail.map(a => a.ux_scores?.[metric] || 0));
                      return (
                        <TableCell key={analysis.analysis_id} className="text-center">
                          <span className={`font-semibold ${isHighest ? 'text-green-600' : getScoreColor(score)}`}>
                            {score}
                            {isHighest && <Trophy className="inline h-3 w-3 ml-1" />}
                          </span>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}

                {/* Overall Scores */}
                <TableRow className="bg-gray-100 dark:bg-gray-900">
                  <TableCell colSpan={comparison.analyses_detail.length + 1} className="font-bold sticky left-0 bg-gray-100 dark:bg-gray-900">
                    Overall Scores
                  </TableCell>
                </TableRow>
                <TableRow className="border-t-2">
                  <TableCell className="sticky left-0 bg-background font-semibold pl-6">
                    AEO Score
                  </TableCell>
                  {comparison.analyses_detail.map((analysis) => {
                    const score = analysis.aeo_score;
                    const isHighest = score === Math.max(...comparison.analyses_detail.map(a => a.aeo_score));
                    return (
                      <TableCell key={analysis.analysis_id} className="text-center">
                        <span className={`font-bold text-lg ${isHighest ? 'text-green-600' : getScoreColor(score)}`}>
                          {score}
                          {isHighest && <Trophy className="inline h-4 w-4 ml-1" />}
                        </span>
                      </TableCell>
                    );
                  })}
                </TableRow>
                <TableRow>
                  <TableCell className="sticky left-0 bg-background font-semibold pl-6">
                    SEO Score
                  </TableCell>
                  {comparison.analyses_detail.map((analysis) => {
                    const score = analysis.seo_score;
                    const isHighest = score === Math.max(...comparison.analyses_detail.map(a => a.seo_score));
                    return (
                      <TableCell key={analysis.analysis_id} className="text-center">
                        <span className={`font-bold text-lg ${isHighest ? 'text-green-600' : getScoreColor(score)}`}>
                          {score}
                          {isHighest && <Trophy className="inline h-4 w-4 ml-1" />}
                        </span>
                      </TableCell>
                    );
                  })}
                </TableRow>
                <TableRow>
                  <TableCell className="sticky left-0 bg-background font-semibold pl-6">
                    UX Score
                  </TableCell>
                  {comparison.analyses_detail.map((analysis) => {
                    const score = analysis.ux_score;
                    const isHighest = score === Math.max(...comparison.analyses_detail.map(a => a.ux_score));
                    return (
                      <TableCell key={analysis.analysis_id} className="text-center">
                        <span className={`font-bold text-lg ${isHighest ? 'text-green-600' : getScoreColor(score)}`}>
                          {score}
                          {isHighest && <Trophy className="inline h-4 w-4 ml-1" />}
                        </span>
                      </TableCell>
                    );
                  })}
                </TableRow>
                <TableRow className="bg-gradient-to-r from-blue-100 to-purple-100 dark:from-blue-950/40 dark:to-purple-950/40 border-t-4 border-blue-500">
                  <TableCell className="sticky left-0 bg-gradient-to-r from-blue-100 to-purple-100 dark:from-blue-950/40 dark:to-purple-950/40 font-bold pl-6">
                    Overall Score
                  </TableCell>
                  {comparison.analyses_detail.map((analysis) => {
                    const score = analysis.overall_score;
                    const isHighest = score === Math.max(...comparison.analyses_detail.map(a => a.overall_score));
                    return (
                      <TableCell key={analysis.analysis_id} className="text-center">
                        <span className={`font-bold text-2xl ${isHighest ? 'text-green-600' : getScoreColor(score)}`}>
                          {score}
                          {isHighest && <Trophy className="inline h-5 w-5 ml-1 text-yellow-500" />}
                        </span>
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Scientific Metrics Analysis Card */}
      <Card className="border-purple-200 dark:border-purple-900">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FlaskConical className="h-5 w-5 text-purple-600" />
            Scientific AEO Metrics
          </CardTitle>
          <CardDescription>
            Advanced analysis using Semantic Density Index, RAG simulation, and Hallucination Prevention testing
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!scientificMetrics && !loadingScientificMetrics && (
            <div className="text-center py-6">
              <FlaskConical className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground mb-4">
                Run advanced scientific analysis to measure how well each page performs
                in real AI retrieval scenarios.
              </p>
              <Button onClick={runScientificMetrics} variant="default">
                <FlaskConical className="h-4 w-4 mr-2" />
                Run Scientific Analysis
              </Button>
            </div>
          )}

          {loadingScientificMetrics && (
            <div className="flex flex-col items-center justify-center py-12 space-y-4">
              <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
              <div className="text-center">
                <p className="font-medium">Running Scientific Analysis...</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Analyzing semantic density, RAG retrieval, and hallucination prevention
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  This may take 30-60 seconds per page
                </p>
              </div>
            </div>
          )}

          {scientificMetricsError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {scientificMetricsError}
                <Button
                  variant="link"
                  size="sm"
                  onClick={runScientificMetrics}
                  className="ml-2"
                >
                  Try Again
                </Button>
              </AlertDescription>
            </Alert>
          )}

          {scientificMetrics?.comparison?.results && (
            <div className="space-y-6">
              {/* Winners Summary */}
              <div className="grid gap-4 md:grid-cols-4">
                <Card className="bg-purple-50 dark:bg-purple-950/20">
                  <CardContent className="pt-4 text-center">
                    <Target className="h-6 w-6 mx-auto text-purple-600 mb-2" />
                    <p className="text-xs font-medium text-muted-foreground">Overall Winner</p>
                    <p className="text-sm font-bold mt-1 truncate">
                      {scientificMetrics.comparison.winners?.overall || 'N/A'}
                    </p>
                  </CardContent>
                </Card>
                <Card className="bg-blue-50 dark:bg-blue-950/20">
                  <CardContent className="pt-4 text-center">
                    <Brain className="h-6 w-6 mx-auto text-blue-600 mb-2" />
                    <p className="text-xs font-medium text-muted-foreground">Semantic Density</p>
                    <p className="text-sm font-bold mt-1 truncate">
                      {scientificMetrics.comparison.winners?.semanticDensity || 'N/A'}
                    </p>
                  </CardContent>
                </Card>
                <Card className="bg-green-50 dark:bg-green-950/20">
                  <CardContent className="pt-4 text-center">
                    <Zap className="h-6 w-6 mx-auto text-green-600 mb-2" />
                    <p className="text-xs font-medium text-muted-foreground">RAG Retrieval</p>
                    <p className="text-sm font-bold mt-1 truncate">
                      {scientificMetrics.comparison.winners?.ragRetrieval || 'N/A'}
                    </p>
                  </CardContent>
                </Card>
                <Card className="bg-orange-50 dark:bg-orange-950/20">
                  <CardContent className="pt-4 text-center">
                    <Shield className="h-6 w-6 mx-auto text-orange-600 mb-2" />
                    <p className="text-xs font-medium text-muted-foreground">Hallucination Shield</p>
                    <p className="text-sm font-bold mt-1 truncate">
                      {scientificMetrics.comparison.winners?.hallucinationPrevention || 'N/A'}
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Scores Comparison Table */}
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[200px]">Metric</TableHead>
                      {scientificMetrics.comparison.results.map((result) => (
                        <TableHead key={result.url} className="text-center min-w-[120px]">
                          <div className="space-y-1">
                            <Badge
                              variant={result.pageType === 'generated' ? 'default' : 'secondary'}
                              className="text-xs"
                            >
                              {result.label}
                            </Badge>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 px-2 text-xs"
                              onClick={() => downloadPageHtml(result.url, result.label)}
                              title={`Download HTML for ${result.label}`}
                            >
                              <Download className="h-3 w-3 mr-1" />
                              HTML
                            </Button>
                          </div>
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {/* Composite Score */}
                    <TableRow className="bg-purple-50 dark:bg-purple-950/20 font-semibold">
                      <TableCell className="font-bold">Composite Score</TableCell>
                      {scientificMetrics.comparison.results.map((result) => {
                        const isWinner = result.label === scientificMetrics.comparison.winners?.overall;
                        return (
                          <TableCell key={result.url} className="text-center">
                            <span className={`text-lg font-bold ${isWinner ? 'text-green-600' : ''}`}>
                              {result.scores.composite}
                              {isWinner && <Trophy className="inline h-4 w-4 ml-1 text-yellow-500" />}
                            </span>
                          </TableCell>
                        );
                      })}
                    </TableRow>

                    {/* Core Metrics */}
                    <TableRow>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Brain className="h-4 w-4 text-blue-600" />
                          Semantic Density
                        </div>
                      </TableCell>
                      {scientificMetrics.comparison.results.map((result) => {
                        const isWinner = result.label === scientificMetrics.comparison.winners?.semanticDensity;
                        return (
                          <TableCell key={result.url} className="text-center">
                            <span className={`font-semibold ${isWinner ? 'text-green-600' : getScientificScoreColor(result.scores.semanticDensity)}`}>
                              {formatScientificScore(result.scores.semanticDensity)}
                              {isWinner && <Trophy className="inline h-3 w-3 ml-1" />}
                            </span>
                          </TableCell>
                        );
                      })}
                    </TableRow>

                    <TableRow>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="h-4 w-4 text-green-600" />
                          Answer Coverage
                        </div>
                      </TableCell>
                      {scientificMetrics.comparison.results.map((result) => {
                        const isWinner = result.label === scientificMetrics.comparison.winners?.answerCoverage;
                        return (
                          <TableCell key={result.url} className="text-center">
                            <span className={`font-semibold ${isWinner ? 'text-green-600' : getScientificScoreColor(result.scores.answerCoverage)}`}>
                              {formatScientificScore(result.scores.answerCoverage)}
                              {isWinner && <Trophy className="inline h-3 w-3 ml-1" />}
                            </span>
                          </TableCell>
                        );
                      })}
                    </TableRow>

                    <TableRow>
                      <TableCell>Citation Readiness</TableCell>
                      {scientificMetrics.comparison.results.map((result) => (
                        <TableCell key={result.url} className="text-center">
                          <span className={`font-semibold ${getScientificScoreColor(result.scores.citationReadiness)}`}>
                            {formatScientificScore(result.scores.citationReadiness)}
                          </span>
                        </TableCell>
                      ))}
                    </TableRow>

                    <TableRow>
                      <TableCell>Spec Completeness</TableCell>
                      {scientificMetrics.comparison.results.map((result) => (
                        <TableCell key={result.url} className="text-center">
                          <span className={`font-semibold ${getScientificScoreColor(result.scores.specCompleteness)}`}>
                            {formatScientificScore(result.scores.specCompleteness)}
                          </span>
                        </TableCell>
                      ))}
                    </TableRow>

                    {/* Advanced Metrics */}
                    {scientificMetrics.comparison.results.some(r => r.advancedMetrics?.ragRetrieval) && (
                      <>
                        <TableRow className="bg-green-50 dark:bg-green-950/20">
                          <TableCell colSpan={scientificMetrics.comparison.results.length + 1} className="font-bold">
                            <div className="flex items-center gap-2">
                              <Zap className="h-4 w-4 text-green-600" />
                              RAG Retrieval Simulation
                            </div>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="pl-6">Retrieval Score</TableCell>
                          {scientificMetrics.comparison.results.map((result) => {
                            const ragScore = result.advancedMetrics?.ragRetrieval?.score || 0;
                            const isWinner = result.label === scientificMetrics.comparison.winners?.ragRetrieval;
                            return (
                              <TableCell key={result.url} className="text-center">
                                <span className={`font-semibold ${isWinner ? 'text-green-600' : getScientificScoreColor(ragScore)}`}>
                                  {formatScientificScore(ragScore)}
                                  {isWinner && <Trophy className="inline h-3 w-3 ml-1" />}
                                </span>
                              </TableCell>
                            );
                          })}
                        </TableRow>
                        <TableRow>
                          <TableCell className="pl-6 text-muted-foreground text-sm">Avg Relevance</TableCell>
                          {scientificMetrics.comparison.results.map((result) => (
                            <TableCell key={result.url} className="text-center text-sm text-muted-foreground">
                              {result.advancedMetrics?.ragRetrieval?.avgRelevance?.toFixed(2) || '-'}
                            </TableCell>
                          ))}
                        </TableRow>
                      </>
                    )}

                    {scientificMetrics.comparison.results.some(r => r.advancedMetrics?.hallucinationPrevention) && (
                      <>
                        <TableRow className="bg-orange-50 dark:bg-orange-950/20">
                          <TableCell colSpan={scientificMetrics.comparison.results.length + 1} className="font-bold">
                            <div className="flex items-center gap-2">
                              <Shield className="h-4 w-4 text-orange-600" />
                              Hallucination Prevention
                            </div>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="pl-6">Prevention Score</TableCell>
                          {scientificMetrics.comparison.results.map((result) => {
                            const hpScore = result.advancedMetrics?.hallucinationPrevention?.score || 0;
                            const isWinner = result.label === scientificMetrics.comparison.winners?.hallucinationPrevention;
                            return (
                              <TableCell key={result.url} className="text-center">
                                <span className={`font-semibold ${isWinner ? 'text-green-600' : getScientificScoreColor(hpScore)}`}>
                                  {formatScientificScore(hpScore)}
                                  {isWinner && <Trophy className="inline h-3 w-3 ml-1" />}
                                </span>
                              </TableCell>
                            );
                          })}
                        </TableRow>
                        <TableRow>
                          <TableCell className="pl-6 text-muted-foreground text-sm">Factual Grounding</TableCell>
                          {scientificMetrics.comparison.results.map((result) => (
                            <TableCell key={result.url} className="text-center text-sm text-muted-foreground">
                              {result.advancedMetrics?.hallucinationPrevention?.factualGrounding?.toFixed(2) || '-'}
                            </TableCell>
                          ))}
                        </TableRow>
                      </>
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* Insights */}
              {scientificMetrics.insights && scientificMetrics.insights.length > 0 && (
                <div className="space-y-3">
                  <h4 className="text-sm font-medium flex items-center gap-2">
                    <Lightbulb className="h-4 w-4 text-yellow-500" />
                    Key Insights
                  </h4>
                  <div className="space-y-2">
                    {scientificMetrics.insights.map((insight, idx) => (
                      <Alert
                        key={idx}
                        className={
                          insight.type === 'strength'
                            ? 'border-green-200 bg-green-50 dark:bg-green-950/20'
                            : insight.type === 'weakness'
                            ? 'border-red-200 bg-red-50 dark:bg-red-950/20'
                            : 'border-blue-200 bg-blue-50 dark:bg-blue-950/20'
                        }
                      >
                        <AlertDescription className="flex items-start gap-2">
                          {insight.type === 'strength' ? (
                            <TrendingUp className="h-4 w-4 text-green-600 shrink-0 mt-0.5" />
                          ) : insight.type === 'weakness' ? (
                            <TrendingDown className="h-4 w-4 text-red-600 shrink-0 mt-0.5" />
                          ) : (
                            <Lightbulb className="h-4 w-4 text-blue-600 shrink-0 mt-0.5" />
                          )}
                          <div>
                            <span className="font-medium">{insight.metric}:</span>{' '}
                            <span className="text-muted-foreground">{insight.message}</span>
                          </div>
                        </AlertDescription>
                      </Alert>
                    ))}
                  </div>
                </div>
              )}

              {/* Metadata */}
              <div className="flex items-center justify-between text-xs text-muted-foreground border-t pt-4">
                <span>
                  Analysis completed in {(scientificMetrics.metadata.duration_ms / 1000).toFixed(1)}s
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={runScientificMetrics}
                  className="h-8"
                >
                  <RefreshCw className="h-3 w-3 mr-1" />
                  Re-run Analysis
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Manual Content Form Dialog */}
      <Dialog
        open={!!showManualForm}
        onOpenChange={(open) => {
          if (!open) {
            setShowManualForm(null);
            setCurrentManualUrl('');
            setCurrentManualHtml('');
          }
        }}
      >
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Add Manual HTML Content
            </DialogTitle>
            <DialogDescription>
              Paste the HTML source from your browser to include this page in a re-run analysis
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription className="text-xs space-y-1">
                <p className="font-medium">How to get HTML source:</p>
                <p>1. Open the page in your browser: <span className="font-mono text-xs break-all">{currentManualUrl}</span></p>
                <p>2. Right-click anywhere → "View Page Source" (Ctrl+U / Cmd+Option+U)</p>
                <p>3. Select All (Ctrl+A / Cmd+A) and Copy (Ctrl+C / Cmd+C)</p>
                <p>4. Paste below</p>
              </AlertDescription>
            </Alert>

            <div>
              <Label>Page URL</Label>
              <Input
                value={currentManualUrl}
                disabled
                className="font-mono text-xs"
              />
            </div>

            <div>
              <Label>HTML Source</Label>
              <Textarea
                placeholder="Paste the complete HTML source here..."
                value={currentManualHtml}
                onChange={(e) => setCurrentManualHtml(e.target.value)}
                rows={12}
                className="font-mono text-xs"
              />
              <p className="text-xs text-muted-foreground mt-1">
                {currentManualHtml.length > 0
                  ? `${currentManualHtml.length.toLocaleString()} characters pasted`
                  : 'Paste HTML source from your browser'}
              </p>
            </div>

            <div className="flex gap-2">
              <Button
                onClick={() => handleAddManualContent(currentManualUrl)}
                disabled={!currentManualHtml.trim()}
              >
                <CheckCircle2 className="h-4 w-4 mr-2" />
                Add Content
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  setShowManualForm(null);
                  setCurrentManualUrl('');
                  setCurrentManualHtml('');
                  setCurrentManualType('');
                  setCurrentManualLabel('');
                }}
              >
                Cancel
              </Button>
            </div>

            {/* Show pending manual content and re-run button */}
            {manualContent.length > 0 && onRerunWithManualContent && (
              <div className="mt-6 p-4 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-200 dark:border-green-900">
                <p className="font-medium text-green-800 dark:text-green-200 mb-2">
                  {manualContent.length} page{manualContent.length > 1 ? 's' : ''} ready for re-analysis:
                </p>
                <ul className="text-sm text-green-700 dark:text-green-300 mb-3 space-y-1">
                  {manualContent.map((item, i) => (
                    <li key={i} className="truncate">• {item.label || getDomainFromUrl(item.url)}</li>
                  ))}
                </ul>
                <Button
                  onClick={() => {
                    setShowManualForm(null);
                    handleRerunAnalysis();
                  }}
                  className="w-full"
                  variant="default"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Re-run Analysis Now
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Re-run Analysis Button */}
      {manualContent.length > 0 && onRerunWithManualContent && (
        <Card className="border-2 border-green-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Ready to re-run analysis</p>
                <p className="text-sm text-muted-foreground">
                  You've added manual content for {manualContent.length} page{manualContent.length > 1 ? 's' : ''}. Click to re-run the analysis including this content.
                </p>
              </div>
              <Button onClick={handleRerunAnalysis} size="lg">
                <RefreshCw className="h-4 w-4 mr-2" />
                Re-run Analysis
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Detailed Analysis Accordion */}
      <Card>
        <CardHeader>
          <CardTitle>Detailed Analysis</CardTitle>
          <CardDescription>
            Strengths and weaknesses for each page
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Accordion type="single" collapsible value={selectedAnalysis || undefined} onValueChange={setSelectedAnalysis}>
            {comparison.analyses_detail.map((analysis) => {
              const failed = isFailedPage(analysis);
              const hasManualContent = manualContent.some(mc => mc.url === analysis.url);
              const domain = getDomainFromUrl(analysis.url);

              return (
              <AccordionItem key={analysis.analysis_id} value={analysis.analysis_id}>
                <AccordionTrigger>
                  <div className="flex items-center gap-2 text-left">
                    <Badge variant={getPageTypeBadge(analysis.page_type)}>
                      {analysis.page_type === 'competitor' ? domain : getPageTypeLabel(analysis.page_type)}
                    </Badge>
                    <span className="truncate max-w-md">{analysis.title}</span>
                    {failed && !hasManualContent && (
                      <Badge variant="destructive" className="text-xs">No Data</Badge>
                    )}
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  {failed && !hasManualContent ? (
                    <div className="space-y-4 pt-4">
                      <Alert variant="destructive">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>
                          <p className="font-medium">Page Could Not Be Analyzed</p>
                          <p className="text-sm mt-1">
                            This page returned an error or no content. Click the button below to manually provide the HTML source for analysis.
                          </p>
                        </AlertDescription>
                      </Alert>

                      <Button
                        onClick={() => {
                          setShowManualForm(analysis.analysis_id);
                          setCurrentManualUrl(analysis.url);
                          setCurrentManualType(analysis.page_type);
                          setCurrentManualLabel(getPageTypeLabel(analysis.page_type));
                          setSelectedAnalysis(null);
                        }}
                        className="w-full"
                      >
                        <FileText className="h-4 w-4 mr-2" />
                        Add Manual HTML Content
                      </Button>
                    </div>
                  ) : (
                  <div className="space-y-4 pt-4">
                    {/* Score Breakdown */}
                    <div className="grid gap-4 md:grid-cols-3">
                      <div>
                        <div className="text-sm font-medium mb-2">AEO Score</div>
                        <div className="space-y-1">
                          {Object.entries(analysis.aeo_scores || {}).map(([key, value]) => (
                            <div key={key} className="flex justify-between text-sm">
                              <span className="text-muted-foreground capitalize">
                                {key.replace(/_/g, ' ')}
                              </span>
                              <span className={getScoreColor(value as number)}>
                                {value}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div>
                        <div className="text-sm font-medium mb-2">SEO Score</div>
                        <div className="space-y-1">
                          {Object.entries(analysis.seo_scores || {}).map(([key, value]) => (
                            <div key={key} className="flex justify-between text-sm">
                              <span className="text-muted-foreground capitalize">
                                {key.replace(/_/g, ' ')}
                              </span>
                              <span className={getScoreColor(value as number)}>
                                {value}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div>
                        <div className="text-sm font-medium mb-2">UX Score</div>
                        <div className="space-y-1">
                          {Object.entries(analysis.ux_scores || {}).map(([key, value]) => (
                            <div key={key} className="flex justify-between text-sm">
                              <span className="text-muted-foreground capitalize">
                                {key.replace(/_/g, ' ')}
                              </span>
                              <span className={getScoreColor(value as number)}>
                                {value}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Strengths and Weaknesses */}
                    <div className="grid gap-4 md:grid-cols-2">
                      <div>
                        <div className="text-sm font-medium mb-2 flex items-center gap-2 text-green-600">
                          <CheckCircle2 className="h-4 w-4" />
                          Strengths
                        </div>
                        <ul className="space-y-1">
                          {analysis.strengths?.map((strength, i) => (
                            <li key={i} className="text-sm text-muted-foreground flex gap-2">
                              <span className="text-green-600">•</span>
                              {strength}
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div>
                        <div className="text-sm font-medium mb-2 flex items-center gap-2 text-red-600">
                          <XCircle className="h-4 w-4" />
                          Weaknesses
                        </div>
                        <ul className="space-y-1">
                          {analysis.weaknesses?.map((weakness, i) => (
                            <li key={i} className="text-sm text-muted-foreground flex gap-2">
                              <span className="text-red-600">•</span>
                              {weakness}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-2">
                      <Button
                        variant="default"
                        size="sm"
                        onClick={() => fetchContextualExamples(analysis.analysis_id)}
                        className="flex-1"
                      >
                        <Lightbulb className="h-4 w-4 mr-2" />
                        Show Specific Examples
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(analysis.url, '_blank')}
                        className="flex-1"
                      >
                        <ExternalLink className="h-4 w-4 mr-2" />
                        View Page
                      </Button>
                    </div>
                  </div>
                  )}
                </AccordionContent>
              </AccordionItem>
              );
            })}
          </Accordion>
        </CardContent>
      </Card>

      {/* Contextual Examples Dialog */}
      <Dialog open={showExamplesDialog} onOpenChange={setShowExamplesDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lightbulb className="h-5 w-5 text-yellow-500" />
              Contextual Examples & Insights
            </DialogTitle>
            <DialogDescription>
              {contextualExamples?.pageTitle && (
                <span className="block mt-1">
                  Specific examples from: <strong>{contextualExamples.pageTitle}</strong>
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          {loadingExamples ? (
            <div className="flex flex-col items-center justify-center py-12 space-y-4">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                Analyzing page content and generating specific examples...
              </p>
              <p className="text-xs text-muted-foreground">
                This may take 10-20 seconds
              </p>
            </div>
          ) : examplesError ? (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{examplesError}</AlertDescription>
            </Alert>
          ) : contextualExamples ? (
            <ScrollArea className="h-[60vh] pr-4">
              <div className="space-y-6">
                {/* Strengths Examples */}
                {contextualExamples.examples.strengths_examples?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium mb-3 flex items-center gap-2 text-green-600">
                      <CheckCircle2 className="h-4 w-4" />
                      Strengths with Examples ({contextualExamples.examples.strengths_examples.length})
                    </h4>
                    <div className="space-y-4">
                      {contextualExamples.examples.strengths_examples.map((item, idx) => (
                        <Card key={idx} className="bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-900">
                          <CardContent className="pt-4 space-y-3">
                            <div className="flex items-start gap-2">
                              <Badge variant="outline" className="text-xs bg-green-100 dark:bg-green-900 shrink-0">
                                {item.category}
                              </Badge>
                              <p className="text-sm font-medium text-green-800 dark:text-green-200">
                                {item.original}
                              </p>
                            </div>
                            <div className="ml-4 space-y-2">
                              <div className="flex items-start gap-2">
                                <Quote className="h-4 w-4 text-green-600 shrink-0 mt-0.5" />
                                <p className="text-sm italic text-muted-foreground">
                                  "{item.example}"
                                </p>
                              </div>
                              <div className="flex items-start gap-2">
                                <Sparkles className="h-4 w-4 text-green-600 shrink-0 mt-0.5" />
                                <p className="text-sm text-green-700 dark:text-green-300">
                                  {item.explanation}
                                </p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}

                {/* Weaknesses Examples */}
                {contextualExamples.examples.weaknesses_examples?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium mb-3 flex items-center gap-2 text-red-600">
                      <XCircle className="h-4 w-4" />
                      Weaknesses with Examples ({contextualExamples.examples.weaknesses_examples.length})
                    </h4>
                    <div className="space-y-4">
                      {contextualExamples.examples.weaknesses_examples.map((item, idx) => (
                        <Card key={idx} className="bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-900">
                          <CardContent className="pt-4 space-y-3">
                            <div className="flex items-start gap-2">
                              <Badge variant="outline" className="text-xs bg-red-100 dark:bg-red-900 shrink-0">
                                {item.category}
                              </Badge>
                              <p className="text-sm font-medium text-red-800 dark:text-red-200">
                                {item.original}
                              </p>
                            </div>
                            <div className="ml-4 space-y-2">
                              <div className="flex items-start gap-2">
                                <Quote className="h-4 w-4 text-red-600 shrink-0 mt-0.5" />
                                <p className="text-sm italic text-muted-foreground">
                                  "{item.example}"
                                </p>
                              </div>
                              <div className="flex items-start gap-2">
                                <AlertCircle className="h-4 w-4 text-red-600 shrink-0 mt-0.5" />
                                <p className="text-sm text-red-700 dark:text-red-300">
                                  {item.explanation}
                                </p>
                              </div>
                              {item.suggestion && (
                                <div className="flex items-start gap-2 mt-2 p-2 bg-amber-50 dark:bg-amber-950/30 rounded">
                                  <Lightbulb className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                                  <p className="text-sm text-amber-800 dark:text-amber-200">
                                    <strong>Suggestion:</strong> {item.suggestion}
                                  </p>
                                </div>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </ScrollArea>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
